<template>
  <div class="is-flex is-align-items-center is-flex-direction-column p-6">
    <!-- BOX STARTS -->
    <div class="payment-request-header">
      <p class="is-family-secondary is-size-4 is-denim mr-3">
        {{ $t("paymentRequest.request-payment") }}
      </p>
      <Loading v-if="loading" isAbsolute isSmall isRight />
    </div>

    <!-- Payment method selector, only shown when there is a choice to make -->
    <div v-if="paymentMethods.length > 1" class="column is-one-third has-text-centered">
      <p class="pt-1 pb-2 has-text-centered is-size-6 is-family-secondary">
        <span class="is-denim has-text-weight-light">{{ $t("paymentRequest.payment-method") }}:</span>
      </p>
      <div class="select is-fullwidth mb-4">
        <select id="payment-request-method" v-model="selectedMethod">
          <option v-for="entry in paymentMethods" :key="entry.method" :value="entry.method">
            {{ optionLabel(entry) }}
          </option>
        </select>
      </div>
      <p v-if="selectedEntry && !isEligible(selectedEntry)" class="has-text-danger-dark is-size-7 mb-4">
        {{ $t("paymentRequest.below-minimum", {
          amount: formatCurrencyLocalized(locale, selectedEntry.minimumAmount,
            selectedEntry.currency)
        }) }}
      </p>
    </div>

    <!-- If email is already entered -->
    <template v-if="paymentEmail != '' && !changing">
      <p class="has-text-weight-light mb-4 is-denim">
        {{ $t("paymentRequest.confirm-email") }}
      </p>
      <div class="column is-one-third has-text-centered">
        <p class="pt-1 pb-4 has-text-centered is-size-5 is-family-secondary">
          <span class="is-denim has-text-weight-light">{{ $t("paymentRequest.current-email") }}:</span>{{ " " }}
          <span class="is-sunrise">{{ paymentEmail }}</span>
        </p>
      </div>
      <span class="is-flex is-justify-content-space-between is-flex-wrap-wrap">
        <button class="button is-primary is-size-6 mx-3 mb-1" style="min-width: 215px"
          :disabled="selectedEntry && !isEligible(selectedEntry)" @click="confirmRequest">
          {{ $t("paymentRequest.confirm-request") }}
        </button>

        <!-- Change payment email -->
        <button class="button is-light is-size-6 mx-3 mb-1" @click="toggleChanging" style="min-width: 215px">
          {{ $t("paymentRequest.change-email") }}
        </button>
      </span>
    </template>

    <!-- If email is yet to be entered -->
    <template v-else>
      <p class="has-text-weight-light mb-2 is-denim">
        {{ $t("paymentRequest.enter-email") }}
      </p>
      <hr class="is-back-sunflower with-space is-full-width" />
      <form @submit.prevent="handlePaymentEmailChange">
        <input id="payment-request-email" name="email" type="email" v-model="paymentEmailInput"
          :placeholder="$t('paymentRequest.payment-email')" autocomplete="email" inputmode="email" autocapitalize="off"
          spellcheck="false" class="pt-1 pb-1 mt-3 input is-size-6" required @blur="checkPaymentEmailTypo"
          @input="paymentEmailSuggestion = null" />
        <p v-if="paymentEmailSuggestion" class="email-suggestion has-text-centered">
          Did you mean
          <button type="button" class="email-suggestion-link" @click="applyPaymentEmailSuggestion">{{
            paymentEmailSuggestion }}</button>?
        </p>
        <input id="payment-request-confirm-email" name="confirm-email" type="email" v-model="confPaymentEmailInput"
          :placeholder="$t('paymentRequest.confirm-payment-email')" inputmode="email" autocapitalize="off"
          spellcheck="false" class="pt-1 pb-1 mt-3 input is-size-6" required onpaste="return false;"
          ondrop="return false;" autocomplete="off" @blur="checkConfPaymentEmailTypo"
          @input="confPaymentEmailSuggestion = null" />
        <p v-if="confPaymentEmailSuggestion" class="email-suggestion has-text-centered mb-3">
          Did you mean
          <button type="button" class="email-suggestion-link" @click="applyConfPaymentEmailSuggestion">{{
            confPaymentEmailSuggestion }}</button>?
        </p>
        <span class="is-flex is-justify-content-space-around">
          <button v-if="changing" class="button is-info m-3 is-size-6" type="submit" @click="toggleChanging">
            {{ $t("cancel") }}
          </button>
          <button class="button is-info m-3 is-size-6" type="submit" @click="showModal2 = true">
            {{ $t("confirm") }}
          </button>
        </span>
      </form>
    </template>

    <div class="is-flex is-justify-content-center p-2">
      <p :class="{
        'has-text-danger-dark': paymentEmailChangeMessage['type'] == 'err',
        'has-text-success-dark': paymentEmailChangeMessage['type'] == 'msg',
      }">
        {{ paymentEmailChangeMessage.value }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { getSuggestedEmail } from "@/revamp/utils";
import { ref, computed, onMounted, watch } from "vue";
import { AuthService } from "@/revamp/firebase/config";
import { useFirebase } from "@/revamp/composables";
import { Loading } from "@/revamp/components";
import { logger, formatCurrencyLocalized } from "@/revamp/utils";

import { useI18n } from "vue-i18n";

const emits = defineEmits(["createModal", "sendVerification", "update"]);

const { t, locale } = useI18n();
const api = useFirebase();

const user = ref({});

const loading = ref(false);
const changing = ref(false);

const paymentEmailInput = ref("");
const confPaymentEmailInput = ref("");
const paymentEmailChangeMessage = ref("");

const submitRequestMessage = ref("");

const paymentEmail = ref("");
const currBalance = ref(0);

const paymentMethods = ref([]);
const selectedMethod = ref("");
const selectedEntry = computed(() => paymentMethods.value.find((entry) => entry.method === selectedMethod.value));

const methodName = (entry) => entry.translations?.[locale.value]?.name || entry.translations?.en?.name || entry.method;
const isEligible = (entry) => currBalance.value >= entry.minimumAmount;
const optionLabel = (entry) =>
  isEligible(entry)
    ? methodName(entry)
    : `${methodName(entry)} (${t("paymentRequest.min-balance", { amount: formatCurrencyLocalized(locale.value, entry.minimumAmount, entry.currency) })})`;

const showConfirmModal = ref(false);
const submitClicked = ref(false);
const submitSuccess = ref(false);
const paymentEmailSuggestion = ref(null);
const confPaymentEmailSuggestion = ref(null);
const checkPaymentEmailTypo = () => {
  if (!paymentEmailInput.value?.trim()) {
    paymentEmailSuggestion.value = null;
    return;
  }

  paymentEmailSuggestion.value = getSuggestedEmail(paymentEmailInput.value);
};
const checkConfPaymentEmailTypo = () => {
  if (!confPaymentEmailInput.value?.trim()) {
    confPaymentEmailSuggestion.value = null;
    return;
  }

  confPaymentEmailSuggestion.value = getSuggestedEmail(confPaymentEmailInput.value);
};
const applyPaymentEmailSuggestion = () => {
  paymentEmailInput.value = paymentEmailSuggestion.value;
  paymentEmailSuggestion.value = null;
};
const applyConfPaymentEmailSuggestion = () => {
  confPaymentEmailInput.value = confPaymentEmailSuggestion.value;
  confPaymentEmailSuggestion.value = null;
};

async function init() {
  loading.value = true;
  let authUser = AuthService.currentUser;
  user.value = await api.getUser(authUser.uid);
  paymentEmail.value = user.value.eTransferEmail ?? "";
  currBalance.value = user.value.balance ?? 0;

  paymentMethods.value = await api.getUserPaymentMethods();
  if (paymentMethods.value.length > 0 && !paymentMethods.value.some((entry) => entry.method === selectedMethod.value)) {
    selectedMethod.value = paymentMethods.value[0].method;
  }

  loading.value = false;
}

onMounted(async () => {
  init();
});

watch(paymentEmailChangeMessage, () => {
  init();
});

const toggleChanging = () => {
  paymentEmailInput.value = "";
  confPaymentEmailInput.value = "";
  paymentEmailSuggestion.value = null;
  confPaymentEmailSuggestion.value = null;
  changing.value = !changing.value;
};

const confirmRequest = () => {
  if (currBalance.value <= 0) {
    emits("createModal", {
      title: t("oops"),
      body: t("paymentRequest.not-enough"),
      buttons: [
        {
          text: t("ok"),
          action: () => emits("createModal", {}),
        },
      ],
    });
    return;
  }

  if (selectedEntry.value && !isEligible(selectedEntry.value)) {
    emits("createModal", {
      title: t("oops"),
      body: t("paymentRequest.below-minimum", { amount: formatCurrencyLocalized(locale.value, selectedEntry.value.minimumAmount, selectedEntry.value.currency) }),
      buttons: [
        {
          text: t("ok"),
          action: () => emits("createModal", {}),
        },
      ],
    });
    return;
  }

  if (!user.value.emailVerified) {
    emits("createModal", {
      title: t("notice"),
      body: t("paymentRequest.verify"),
      buttons: [
        {
          text: t("close"),
          action: () => {
            emits("createModal", {});
          },
        },
        {
          text: t("send"),
          action: () => {
            emits("createModal", {});
            emits("sendVerification");
          },
        },
      ],
    });
    return;
  }

  emits("createModal", {
    title: t("confirmation"),
    body: selectedEntry.value
      ? t("paymentRequest.sentWithMethod", {
        currBalance: formatCurrencyLocalized(locale.value, currBalance.value, user.value.currency),
        methodName: methodName(selectedEntry.value),
        paymentEmail: paymentEmail.value,
      })
      : t("paymentRequest.sent", {
        currBalance: formatCurrencyLocalized(locale.value, currBalance.value, user.value.currency),
        paymentEmail: paymentEmail.value,
      }),
    buttons: [
      {
        text: t("close"),
        action: () => {
          emits("createModal", {});
        },
      },
      {
        text: t("confirm"),
        action: () => {
          emits("createModal", {});
          submitRequest();
        },
      },
    ],
  });
  return;
};

const handlePaymentEmailChange = async () => {
  paymentEmailChangeMessage.value = "";

  if (paymentEmailInput.value != confPaymentEmailInput.value) {
    paymentEmailChangeMessage.value = {
      value: t("paymentRequest.match"),
      type: "err",
    };
    return;
  }

  if (paymentEmail.value == "" && paymentEmailInput.value == "") {
    paymentEmailChangeMessage.value = {
      value: t("paymentRequest.makeSure"),
      type: "err",
    };
    return;
  }

  loading.value = true;

  try {
    await api.changePaymentEmail(user.value.uid, paymentEmailInput.value);
    paymentEmailChangeMessage.value = {
      value: t("paymentRequest.updated"),
      type: "msg",
    };
    setTimeout(() => {
      paymentEmailChangeMessage.value = "";
    }, 3000);
    paymentEmailInput.value = "";
    confPaymentEmailInput.value = "";
    paymentEmailSuggestion.value = null;
    confPaymentEmailSuggestion.value = null;
  } catch (err) {
    paymentEmailChangeMessage.value = {
      value: t("paymentRequest.failed"),
      type: "err",
    };
  } finally {
    loading.value = false;
    changing.value = false;
  }
};

//Function that handles payment Request
const submitRequest = async () => {
  submitRequestMessage.value = {
    value: "",
    type: "msg",
  };

  submitClicked.value = true;
  submitSuccess.value = false;
  loading.value = true;

  try {
    await api.requestPayout(user.value.uid, selectedMethod.value || undefined);
    submitRequestMessage.value = {
      value: t("paymentRequest.request"),
      type: "msg",
    };
    submitClicked.value = false;
    submitSuccess.value = true;
    emits("update");
    await init();
  } catch (e) {
    logger.error(`Failed to request payment: ${e.toString()}`);
    submitRequestMessage.value = {
      value: t("paymentRequest.failedRequest"),
      type: "err",
    };
    submitClicked.value = false;
  }
  loading.value = false;
};
</script>

<style scoped lang="scss">
@import "@/revamp/assets/scss/elements.scss";

#input_field {
  background-color: #e1e1e1;
  border: none;
}

.storm-cloud {
  background-color: #717171;
  width: 90%;
  border-radius: 50px;

  &.with-space {
    margin: 25px auto;
  }
}

.header-row {
  width: 100%;
  max-height: 50px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.payment-request-header {
  @extend .header-row;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 1.5rem;
}
</style>
